import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider as MuiDivider,
  FormControlLabel,
  Link,
  Paper as MuiPaper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { format } from "date-fns";
import useAuth from "../../../../hooks/useAuth";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import useAppSelector from "../../../../hooks/useAppSelector";
import {
  BankConnectionStatus,
  fetchBankAccounts,
  fetchBankConnections,
  initiateConnect,
  revokeConnection,
  triggerSync,
} from "../../../../redux/slices/bankConnection";
import useSnackbar from "../../SnackbarHook";
import CustomSnackbar from "../../SnackBar";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Button = styled(MuiButton)(spacing);

const PROVIDERS: { key: string; label: string; description: string }[] = [
  {
    key: "nordea",
    label: "Nordea",
    description:
      "Yhteys muodostetaan Nordean verkkopankin kautta (vaatii kirjautumisen).",
  },
  {
    key: "op",
    label: "OP (Osuuspankki)",
    description: "Yhteys muodostetaan suoraan OP:n yritystilijärjestelmään.",
  },
  {
    key: "spankki",
    label: "S-Pankki",
    description:
      "Yhteys muodostetaan S-Pankin verkkopankin kautta (vaatii kirjautumisen).",
  },
  {
    key: "aktia",
    label: "Aktia",
    description:
      "Yhteys muodostetaan Aktian verkkopankin kautta (vaatii kirjautumisen).",
  },
  {
    key: "poppankki",
    label: "POP Pankki",
    description:
      "Yhteys muodostetaan POP Pankin verkkopankin kautta (vaatii kirjautumisen).",
  },
  {
    key: "alandsbank",
    label: "Ålandsbanken",
    description:
      "Yhteys muodostetaan Ålandsbankenin verkkopankin kautta (vaatii kirjautumisen).",
  },
];

function providerLabel(key: string): string {
  return PROVIDERS.find((p) => p.key === key)?.label ?? key;
}

function statusChip(status: BankConnectionStatus) {
  switch (status) {
    case "active":
      return <Chip size="small" label="Aktiivinen" color="success" />;
    case "pending":
      return <Chip size="small" label="Odottaa" color="info" />;
    case "expired":
      return <Chip size="small" label="Vanhentunut" color="error" />;
    case "revoked":
      return <Chip size="small" label="Peruutettu" color="default" />;
  }
}

export default function BankConnectionPage() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { connections, accounts, status, syncingAccountId } = useAppSelector(
    (state) => state.bankConnection
  );

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [selectedProvider, setSelectedProvider] = useState("nordea");
  const [connectLoading, setConnectLoading] = useState(false);
  const [revokeDialogId, setRevokeDialogId] = useState<string | null>(null);

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    duration,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
  } = useSnackbar();

  const showError = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  };

  useEffect(() => {
    if (!associationId) return;
    dispatch(fetchBankConnections(associationId));
    dispatch(fetchBankAccounts(associationId));

    // Handle redirect back from an authorization_code provider (e.g. Nordea)
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") {
      dispatch(fetchBankConnections(associationId));
      dispatch(fetchBankAccounts(associationId));
    }
  }, [dispatch, associationId]);

  const activeConnection = connections.find((c) => c.status === "active");
  const expiredConnection = connections.find((c) => c.status === "expired");

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const result = await dispatch(
        initiateConnect({ associationId, provider: selectedProvider })
      ).unwrap();

      if ("authUrl" in result) {
        // authorization_code flow (Nordea): redirect user to bank login page
        window.location.href = result.authUrl;
      } else {
        // client_credentials flow (OP): connection established immediately
        dispatch(fetchBankConnections(associationId));
        dispatch(fetchBankAccounts(associationId));
        setConnectLoading(false);
      }
    } catch {
      setConnectLoading(false);
      showError("Yhteyden muodostaminen epäonnistui");
    }
  };

  const handleRevoke = async () => {
    if (!revokeDialogId) return;
    await dispatch(revokeConnection({ id: revokeDialogId, associationId }));
    setRevokeDialogId(null);
    dispatch(fetchBankConnections(associationId));
  };

  const handleSync = async (accountId: string) => {
    await dispatch(triggerSync({ id: accountId, associationId }));
    setTimeout(() => dispatch(fetchBankAccounts(associationId)), 2000);
  };

  const isLoading = status === "loading";

  // Matches the backend warning threshold in bankSync worker — both must move
  // together if either is tuned.
  const CONSENT_EXPIRY_WARN_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

  // Consent expiry warning only applies to authorization_code providers (Nordea)
  const consentWarnDate = activeConnection?.consentExpiresAt
    ? new Date(activeConnection.consentExpiresAt)
    : null;
  const consentExpiresSoon =
    consentWarnDate != null &&
    consentWarnDate.getTime() - Date.now() < CONSENT_EXPIRY_WARN_WINDOW_MS;

  return (
    <>
      <Helmet title="Pankkiyhteys" />
      <Typography variant="h3" gutterBottom>
        Pankkiyhteys
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/accounting/bank/transactions">
          Talous
        </Link>
        <Typography>Pankkiyhteys</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {/* Connection status card */}
      <Card mb={4}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pankkiyhteys
          </Typography>

          {isLoading && connections.length === 0 ? (
            <CircularProgress size={24} />
          ) : activeConnection ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {statusChip(activeConnection.status)}
                <Typography variant="body2">
                  {providerLabel(activeConnection.provider)}
                </Typography>
                {activeConnection.consentExpiresAt && (
                  <Typography variant="body2" color="text.secondary">
                    — lupa vanhenee{" "}
                    {format(
                      new Date(activeConnection.consentExpiresAt),
                      "dd.MM.yyyy"
                    )}
                  </Typography>
                )}
              </Box>

              {consentExpiresSoon && (
                <Alert severity="warning">
                  Pankkiyhteys vanhenee pian. Uusi lupa klikkaamalla "Uusi lupa"
                  alla.
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setRevokeDialogId(activeConnection.id)}
                >
                  Poista yhteys
                </Button>
                {consentExpiresSoon && (
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={handleConnect}
                    disabled={connectLoading}
                  >
                    Uusi lupa
                  </Button>
                )}
              </Box>
            </Box>
          ) : expiredConnection ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Alert severity="error">
                {providerLabel(expiredConnection.provider)}-yhteys on
                vanhentunut. Muodosta yhteys uudelleen.
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConnect}
                disabled={connectLoading}
                sx={{ alignSelf: "flex-start" }}
              >
                {connectLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Uusi lupa"
                )}
              </Button>
            </Box>
          ) : (
            /* No connection — show bank picker */
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Ei aktiivista pankkiyhteyttä. Valitse pankki ja yhdistä tili
                automaattista täsmäytystä varten.
              </Typography>

              <RadioGroup
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
              >
                {PROVIDERS.map((p) => (
                  <Box key={p.key}>
                    <FormControlLabel
                      value={p.key}
                      control={<Radio />}
                      label={p.label}
                    />
                    {selectedProvider === p.key && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 4, display: "block", mb: 0.5 }}
                      >
                        {p.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </RadioGroup>

              <Button
                variant="contained"
                color="primary"
                onClick={handleConnect}
                disabled={connectLoading}
                sx={{ alignSelf: "flex-start" }}
              >
                {connectLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Yhdistä ${providerLabel(selectedProvider)}`
                )}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Accounts table */}
      {accounts.length > 0 && (
        <Card mb={4}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pankkitilit
            </Typography>
          </CardContent>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tili</TableCell>
                  <TableCell>IBAN</TableCell>
                  <TableCell>Valuutta</TableCell>
                  <TableCell>Viimeisin synkronointi</TableCell>
                  <TableCell>Toiminnot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((acct) => (
                  <TableRow key={acct.id} hover>
                    <TableCell>{acct.accountName ?? "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {acct.iban
                        ? `${acct.iban.slice(0, 4)} **** **** ${acct.iban.slice(
                            -4
                          )}`
                        : "—"}
                    </TableCell>
                    <TableCell>{acct.currency}</TableCell>
                    <TableCell>
                      {acct.lastSyncedAt
                        ? format(
                            new Date(acct.lastSyncedAt),
                            "dd.MM.yyyy HH:mm"
                          )
                        : "Ei koskaan"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={syncingAccountId === acct.id}
                        onClick={() => handleSync(acct.id)}
                      >
                        {syncingAccountId === acct.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Synkronoi"
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Card>
      )}

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeDialogId} onClose={() => setRevokeDialogId(null)}>
        <DialogTitle>Poista pankkiyhteys?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pankkiyhteyden poistaminen peruuttaa pankin luvan. Olemassa olevat
            pankkitapahtumat ja täsmäytykset säilytetään. Toimintoa ei voi
            peruuttaa.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogId(null)}>Peruuta</Button>
          <Button color="error" onClick={handleRevoke}>
            Poista yhteys
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={() => setOpenSnackbar(false)}
      />
    </>
  );
}
